import { FC, ReactNode } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography, Link as LinkComponent } from '@robocorp/components';
import { styled, keyframes } from '@robocorp/theme';
import { IconRefresh3 } from '@robocorp/icons/iconic';

import astronaut from '~/assets/mark/astronaut.png';
import { ExternalLinks } from '~/constants';
import { RestrictionGuardType } from '../types';

const float = keyframes`
  0% {
    transform: translateY(-7%) rotate(10deg);
  }
  100% {
    transform: translateY(7%) rotate(-5deg);
  }
`;

const Image = styled.img`
  animation: ${float} 14s cubic-bezier(0.37, 0, 0.63, 1) infinite alternate;
  transform-origin: center center;
`;

interface Props {
  description: string;
  instructions?: string;
  title: string;
  children?: ReactNode;
}

/**
 * Full view placeholder component for when a resource (e.g. Workspace or Invitation) is not available
 */
export const Placeholder: FC<Props> = ({ title, description, instructions, children }) => {
  const { t } = useTranslation();

  return (
    <Box margin="0 auto" textAlign="center" width="100%" px={42} py={162} maxWidth={480}>
      <Box mb={32}>
        <Image src={astronaut} width="112" height="137" alt={title} />
      </Box>
      <Typography as="h2">{title}</Typography>
      <Box mt={16} mb={24}>
        <Typography lineHeight="140%">{description}</Typography>
        {instructions && <Typography mt={8}>{instructions}</Typography>}
      </Box>
      {children ?? (
        <Button forwardedAs={Link} to="/">
          {t('goToHome')}
        </Button>
      )}
    </Box>
  );
};

export const SmallPlaceholder: FC<Omit<Props, 'instructions'> & { instructions?: ReactNode }> = ({
  description,
  instructions,
  title,
  children,
}) => {
  const { t } = useTranslation();

  return (
    <Box
      display="flex"
      margin="0 auto"
      width="100%"
      py={162}
      px={42}
      maxWidth={480}
      alignItems="center"
    >
      <Box width="100%">
        <Typography as="h3" mb={8}>
          {title}
        </Typography>

        <Box mt={16} mb={16}>
          <Typography lineHeight="140%">
            {description}
            {` `}
            {instructions}
          </Typography>
        </Box>

        {children ?? (
          <Button forwardedAs={Link} to="/">
            {t('goToHome')}
          </Button>
        )}
      </Box>
      <Image src={astronaut} width={90} height={110} alt={title} />
    </Box>
  );
};

export const RestrictedPlaceholder: FC = () => {
  const { t } = useTranslation();
  return (
    <Placeholder
      title={t('restriction.permission.title')}
      description={t('restriction.permission.description')}
    />
  );
};

export const NotFoundPlaceholder: FC = () => {
  const { t } = useTranslation();
  return (
    <Placeholder
      title={t('restriction.notFound.title')}
      description={t('restriction.notFound.description')}
      instructions={t('restriction.notFound.instructions')}
    />
  );
};

export const WorkspaceNotFoundPlaceholder: FC = () => {
  const { t } = useTranslation();
  return (
    <Placeholder
      title={t('restriction.notFound.workspace.title')}
      description={t('restriction.notFound.workspace.description')}
      instructions={t('restriction.notFound.workspace.instructions')}
    />
  );
};

export const ErrorPlaceholder: FC = () => {
  const { t } = useTranslation();

  const refreshPage = () => {
    document.location.reload();
  };

  return (
    <SmallPlaceholder
      title={t('restriction.error.title')}
      description={t('restriction.error.description')}
      instructions={
        <LinkComponent href={ExternalLinks.SUPPORT}>
          {t('contactSupport').toLowerCase()}
        </LinkComponent>
      }
    >
      <Button onClick={refreshPage} icon={IconRefresh3}>
        {t('refreshPage')}
      </Button>
    </SmallPlaceholder>
  );
};

export const PlaceholderType: FC<{ type?: RestrictionGuardType }> = ({ type }) => {
  if (type === 'redirect') return <Navigate to="/" />;
  if (type === 'notFound') return <NotFoundPlaceholder />;
  if (type === 'hide') return null;
  return <RestrictedPlaceholder />;
};
